import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {IRoleWithPermissions} from "../../../interfaces/IRoleWithPermissions";
import {PermissionsService} from "../../../services/permissions.service";
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {
  ColComponent,
  ContainerComponent,
  ListGroupDirective,
  ListGroupItemDirective,
  RowComponent
} from "@coreui/angular";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {MatIcon} from "@angular/material/icon";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatTooltip} from "@angular/material/tooltip";
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef,
  MatTable
} from "@angular/material/table";
import {RoleSettingsScreenState} from "../../../enum/RoleSettingsScreenState";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatInput} from "@angular/material/input";
import {MatCheckbox} from "@angular/material/checkbox";
import {IPermission} from "../../../interfaces/IPermission";
import {PermissionsSelectorComponent} from "../../../components/permissions-selector/permissions-selector.component";
import {IUpdateRoleWithPermissions} from "../../../interfaces/IUpdateRoleWithPermissions";
import {MatStepperNext} from "@angular/material/stepper";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {SnackbarService} from "../../../services/snackbar.service";
import {AgGridAngular} from "ag-grid-angular";
import {FirstDataRenderedEvent, GridSizeChangedEvent} from "ag-grid-community";
import {AdminUserTables} from "../../../table-definitions/admin-user-tables";
import {MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";

@Component({
  selector: 'app-view-edit-role',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    ContainerComponent,
    RowComponent,
    ColComponent,
    ListGroupItemDirective,
    ListGroupDirective,
    MatCard,
    MatCardTitle,
    MatCardHeader,
    MatCardContent,
    MatIcon,
    MatButton,
    MatTooltip,
    MatTable,
    MatHeaderCell,
    MatColumnDef,
    MatHeaderCellDef,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatHeaderRow,
    MatRow,
    MatRowDef,
    MatHeaderRowDef,
    RouterLink,
    MatFormField,
    FormsModule,
    MatInput,
    MatCheckbox,
    MatLabel,
    PermissionsSelectorComponent,
    MatStepperNext,
    MatProgressSpinner,
    ReactiveFormsModule,
    MatError,
    AgGridAngular,
    DatePipe,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    MatMenuTrigger
  ],
  templateUrl: './view-edit-role.component.html',
  styleUrl: './view-edit-role.component.css'
})
export class ViewEditRoleComponent implements OnInit {
  roleId: string | null = null;
  public roleWithPermissions!: IRoleWithPermissions;
  screenState: RoleSettingsScreenState = RoleSettingsScreenState.RoleSettings;
  screenStateEnum = RoleSettingsScreenState;
  public uiState: UIStateEnum = UIStateEnum.ShowLoading;
  public uiStateEnumForTemplate = UIStateEnum;
  public availablePermissions: IPermission[] = [];
  public selectedPermissions: IPermission[] = [];
  private originalPermissionsList: IPermission[] = [];
  public isEditingRole = false;
  public isEditingPermissions = false;
  public displayedUsersColumns: string[] = ['name', 'email', 'confirmedOn', 'active', 'actions'];
  public roleNameControl: FormControl<string | null> = new FormControl<string | null>('', Validators.required);
  public roleDescriptionControl: FormControl<string | null> = new FormControl<string | null>('', Validators.required);
  public isCrossGameRoleControl: FormControl<boolean | null> = new FormControl<boolean>(false);

  constructor(
    private permissionsService: PermissionsService,
    private route: ActivatedRoute,
    private snackBarService: SnackbarService,
    private router: Router
  ) {
  }

  public updateRoleForm = new FormGroup({
    roleName: this.roleNameControl,
    roleDescription: this.roleDescriptionControl,
    isCrossGameRole: this.isCrossGameRoleControl
  });

  ngOnInit() {
    this.uiState = UIStateEnum.ShowLoading;
    this.roleId = this.route.snapshot.paramMap.get('roleId');
    this.fetchRoleAndPermissions();
  }

  private fetchRoleAndPermissions() {
    if (!this.roleId) return;

    this.permissionsService.getRoleWithPermissions(this.roleId).subscribe({
      next: (roleWithPermissions) => {
        this.roleWithPermissions = roleWithPermissions;

        this.initializeFormValues(roleWithPermissions);

        this.permissionsService.fetchAllAvailablePermissions().subscribe({
          next: (allPermissions) => {
            this.availablePermissions = allPermissions;
            this.selectedPermissions = roleWithPermissions.Permissions;
            this.uiState = UIStateEnum.ShowData;
          },
          error: () => this.uiState = UIStateEnum.ShowData
        });
      },
      error: () => this.uiState = UIStateEnum.ShowData
    });
  }

  private initializeFormValues(roleWithPermissions: IRoleWithPermissions): void {
    this.updateRoleForm.patchValue({
      roleName: roleWithPermissions.Role.Name,
      roleDescription: roleWithPermissions.Role.Description,
      isCrossGameRole: roleWithPermissions.Role.IsCrossGameRole
    });
  }

  toggleEditRole() {
    if (this.isEditingRole) {
      if (this.updateRoleForm.invalid) {
        this.updateRoleForm.markAllAsTouched();
        return;
      }

      this.updateRoleWithPermissions();
    } else {
      this.isEditingRole = true;
    }
  }

  toggleEditPermissions() {
    if (!this.isEditingPermissions) {
      this.originalPermissionsList = this.selectedPermissions.slice();
    } else {
      this.updateRoleWithPermissions();
    }
    this.isEditingPermissions = !this.isEditingPermissions;
  }

  cancelEditRole() {
    this.isEditingRole = false;
  }

  cancelEditPermissions() {
    this.selectedPermissions = this.originalPermissionsList.slice();
    this.isEditingPermissions = false;
  }

  updateRoleWithPermissions() {
    if (!this.roleWithPermissions) return;

    const updatedRole: IUpdateRoleWithPermissions = {
      roleId: this.roleWithPermissions.Role.Id,
      name: this.roleNameControl.value!,
      description: this.roleDescriptionControl.value!,
      isCrossGame: this.isCrossGameRoleControl.value!,
      permissionValues: this.selectedPermissions.map(p => p.Value)
    };

    this.permissionsService.updateRoleWithPermissions(updatedRole).subscribe({
      next: () => {
        this.snackBarService.openSuccessfulSnackBar('Role updated successfully!');
        this.fetchRoleAndPermissions();
        this.isEditingRole = false;
        this.isEditingPermissions = false;
      },
      error: () => {
        this.snackBarService.openErrorSnackBar('Error updating role!');
      }
    });
  }

  viewUserDetails(user: any): void {
    this.router.navigateByUrl(`cross-game-user-details/${user.Id}`);
  }
}
